import { ListSourceType } from "@/enum/list.enum";
import { NewInRoleChangeType } from "@/enum/company-enrich-type.enum";
import { ParallelAiMonitorFrequency } from "@/enum/parallel-ai-type.enum";
import { Industry } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";
import { IcpPosition } from "@/pages/Private/pages/List/pages/ListEditDetails/enum/icp.enum";

/** Location suggestion as the JOBS source expects it. */
export interface JobLocation {
	id: string;
	name: string;
}

/** The three ways the onboarding form can describe a position (see `PositionColumn`). */
export type OnboardingIcpPosition =
	| IcpPosition.CEO_AND_SIMILAR
	| IcpPosition.CUSTOM
	| IcpPosition.DEPARTMENT_SENIORITY;

/**
 * Who to find at each qualifying company — the minified single search profile of
 * the in-app details step (`ListEditDetails`). The company-sourced list types
 * only deliver companies, so without this the created list has nobody to enrich
 * and the user has to finish it at `/app/lists/create/details/:id`.
 *
 * The persona-segment option of the full step is deliberately absent: persona
 * categories are generated after checkout, so there is nothing to pick from yet.
 */
export interface PeopleAtCompanyConfig {
	position: OnboardingIcpPosition;
	/** The single free-text job title of the CUSTOM position. */
	title: string;
	seniority: string[];
	department: string[];
	/** How many people are enriched per matching company (1…3). */
	maxPerCompany: number;
}

export interface HiringConfig {
	kind: "hiring";
	query: string;
	location: JobLocation | null;
	employmentType: string;
	positionTitles: string[];
	peopleAtCompany: PeopleAtCompanyConfig;
}

export interface LookalikeConfig {
	kind: "lookalike";
	domains: string[];
	semanticQuery: string;
	companyCountries: string[];
	employees: string[];
	positionQuery: string[];
	seniority: string[];
	countries: string[];
}

export interface LeadDbConfig {
	kind: "leadDb";
	semanticQuery: string;
	positionQuery: string[];
	seniority: string[];
	department: string[];
	countries: string[];
	industries: Industry[];
	employees: string[];
	companyCountries: string[];
	keywords: string[];
}

export interface NewInRoleConfig {
	kind: "newInRole";
	changeTypes: NewInRoleChangeType[];
	windowPreset: NewInRoleWindowPreset;
	department: string[];
	seniority: string[];
	countries: string[];
	industries: Industry[];
	employees: string[];
}

export interface TopicConfig {
	kind: "topic";
	query: string;
	authorJobTitle: string;
	timeFrame: number;
	capture: TopicCapture;
}

export interface GmapsConfig {
	kind: "gmaps";
	query: string;
	country: string;
	city: string;
	peopleAtCompany: PeopleAtCompanyConfig;
}

export interface AiCompanyConfig {
	kind: "aiCompany";
	query: string;
	peopleAtCompany: PeopleAtCompanyConfig;
}

export interface AiNewsConfig {
	kind: "aiNews";
	query: string;
	frequency: ParallelAiMonitorFrequency;
	peopleAtCompany: PeopleAtCompanyConfig;
}

export interface FundingConfig {
	kind: "funding";
	countries: string[];
	category: string[];
	round: string[];
	lastXDays: number;
	peopleAtCompany: PeopleAtCompanyConfig;
}

export interface MnaConfig {
	kind: "mna";
	countries: string[];
	category: string[];
	lastXDays: number;
	peopleAtCompany: PeopleAtCompanyConfig;
}

/** Which engagements on a matching LinkedIn post are turned into leads. */
export interface TopicCapture {
	posts: boolean;
	likes: boolean;
	comments: boolean;
}

/** Rolling "started within the last N days" window of the new-in-role list. */
export type NewInRoleWindowPreset = "30" | "90" | "180";

export type OnboardingListConfig =
	| HiringConfig
	| LookalikeConfig
	| LeadDbConfig
	| NewInRoleConfig
	| TopicConfig
	| GmapsConfig
	| AiCompanyConfig
	| AiNewsConfig
	| FundingConfig
	| MnaConfig;

export type OnboardingListTypeKey = OnboardingListConfig["kind"];

/**
 * The types whose source only yields companies — they carry the "who to find at
 * each company" profile. The lead-database and LinkedIn types already collect
 * person filters in their own form, so they never ask for it twice.
 */
export type CompanySourcedConfig = Extract<OnboardingListConfig, { peopleAtCompany: unknown }>;

export const isCompanySourced = (config: OnboardingListConfig): config is CompanySourcedConfig =>
	"peopleAtCompany" in config;

/** Catalog sections, in the order the picker renders them. */
export type OnboardingListSection =
	| "linkedinActivity"
	| "leadDatabases"
	| "news"
	| "jobs"
	| "fundingMa";

export interface OnboardingListTypeDef {
	/** i18n suffix under onboarding.lists.types.<key> */
	key: OnboardingListTypeKey;
	sourceType: ListSourceType;
	sectionKey: OnboardingListSection;
}

/**
 * The list types offered during onboarding — the pre-checkout subset of the
 * in-app create catalog (`ListCreate/config/tileConfig.ts`). The types that
 * need a connected LinkedIn account are deliberately absent: nothing is
 * connected before checkout, so they could only be created dormant.
 */
export const ONBOARDING_LIST_TYPES: OnboardingListTypeDef[] = [
	{ key: "topic", sourceType: ListSourceType.LI_POST, sectionKey: "linkedinActivity" },
	{ key: "leadDb", sourceType: ListSourceType.COMPANY_ENRICH, sectionKey: "leadDatabases" },
	{ key: "lookalike", sourceType: ListSourceType.COMPANY_ENRICH, sectionKey: "leadDatabases" },
	{ key: "newInRole", sourceType: ListSourceType.COMPANY_ENRICH, sectionKey: "leadDatabases" },
	{ key: "gmaps", sourceType: ListSourceType.MAPS, sectionKey: "leadDatabases" },
	{ key: "aiCompany", sourceType: ListSourceType.PARALLEL_AI, sectionKey: "leadDatabases" },
	{ key: "aiNews", sourceType: ListSourceType.PARALLEL_AI, sectionKey: "news" },
	{ key: "hiring", sourceType: ListSourceType.JOBS, sectionKey: "jobs" },
	{ key: "funding", sourceType: ListSourceType.FUNDING_ROUNDS, sectionKey: "fundingMa" },
	{ key: "mna", sourceType: ListSourceType.FUNDING_ROUNDS, sectionKey: "fundingMa" },
];

export const ONBOARDING_LIST_SECTIONS: OnboardingListSection[] = [
	"linkedinActivity",
	"leadDatabases",
	"news",
	"jobs",
	"fundingMa",
];

export const ONBOARDING_LISTS_MAX = 2;

/**
 * Fallback recommendation when the model call fails — mirrors the API's own
 * (`list-recommendations.util.ts`). Deliberately without the hiring list: open
 * job ads are only a buying signal for recruiting/staffing companies, and this
 * fallback cannot tell whether this one is one.
 */
export const FALLBACK_FEATURED_KEYS: OnboardingListTypeKey[] = ["leadDb", "lookalike"];
